import React from 'react';
import { PraxisPaper } from '../../types';
import { ExamCatalogEntry } from '../../data/examCatalog';

// =====================================================================
// Exam-family illustrations + shared catalog helpers (2026-10-07 navy
// redesign). Used by the front page (LandingPage.tsx) and the logged-in
// Exams Directory (ExamsDirectory.tsx) so both show the same picture for
// each family. The scenes are original, generic landmark drawings (palms,
// skylines, capitol domes, etc.) authored for this site: no logos and no
// third-party artwork. They are static strings written here, never user
// input, so rendering them with dangerouslySetInnerHTML is safe.
// =====================================================================

const ART: Record<string, (c: [string, string]) => string> = {
  us: (c) => `<svg viewBox="0 0 160 70" width="160" height="70">
    <g fill="${c[1]}"><rect x="8" y="34" width="14" height="36"/><rect x="24" y="22" width="12" height="48"/><rect x="38" y="40" width="16" height="30"/></g>
    <g fill="${c[0]}"><rect x="96" y="46" width="52" height="24"/><rect x="100" y="40" width="44" height="6"/>
    <path d="M104 40a18 14 0 0136 0z"/><rect x="120" y="14" width="4" height="14"/><circle cx="122" cy="12" r="3"/></g>
    <g fill="#fff" opacity=".9"><rect x="104" y="50" width="4" height="16"/><rect x="113" y="50" width="4" height="16"/><rect x="122" y="50" width="4" height="16"/><rect x="131" y="50" width="4" height="16"/><rect x="140" y="50" width="4" height="16"/></g>
    <g fill="${c[0]}"><path d="M70 14l2.4 5 5.4.6-4 3.7 1.1 5.3-4.9-2.7-4.9 2.7 1.1-5.3-4-3.7 5.4-.6z"/><path d="M84 26l1.6 3.3 3.6.4-2.7 2.5.8 3.5-3.3-1.8-3.3 1.8.8-3.5-2.7-2.5 3.6-.4z" opacity=".7"/></g>
  </svg>`,
  ca: (c) => `<svg viewBox="0 0 160 70" width="160" height="70">
    <path d="M0 70 L0 52 Q40 40 80 50 T160 46 L160 70z" fill="${c[1]}" opacity=".5"/>
    <g stroke="${c[0]}" stroke-width="3" fill="none"><path d="M38 70V22"/><path d="M38 22c-12-3-17 4-20 9M38 22c12-4 17 2 20 8M38 22c-5-10-14-10-17-7M38 22c5-10 14-9 16-6"/>
    <path d="M60 70V34"/><path d="M60 34c-9-2-13 3-15 7M60 34c9-3 13 2 15 7M60 34c-4-7-10-7-12-5"/></g>
    <g stroke="${c[0]}" stroke-width="2.5" fill="none"><path d="M96 70V26M138 70V26"/><path d="M84 46Q117 18 150 46"/><path d="M84 52h70"/>
    <path d="M104 46v6M112 40v12M120 37v15M128 40v12"/></g>
  </svg>`,
  pp: (c) => `<svg viewBox="0 0 160 70" width="160" height="70">
    <rect x="70" y="50" width="70" height="10" rx="2" fill="${c[0]}"/><rect x="74" y="40" width="62" height="10" rx="2" fill="${c[1]}"/><rect x="78" y="30" width="54" height="10" rx="2" fill="${c[0]}" opacity=".7"/>
    <path d="M110 30c0-10 8-14 12-10" stroke="${c[0]}" stroke-width="2.5" fill="none"/>
    <circle cx="118" cy="18" r="9" fill="#f43f5e"/><path d="M118 9c2-4 6-5 8-4" stroke="#16a34a" stroke-width="2.5" fill="none"/>
    <g transform="rotate(-30 40 50)"><rect x="20" y="44" width="46" height="9" rx="2" fill="#fbbf24"/><path d="M66 44l10 4.5-10 4.5z" fill="#fde68a"/><rect x="14" y="44" width="6" height="9" rx="1.5" fill="#f472b6"/></g>
  </svg>`,
  ny: (c) => `<svg viewBox="0 0 160 70" width="160" height="70">
    <g fill="${c[1]}"><rect x="14" y="36" width="16" height="34"/><rect x="32" y="26" width="14" height="44"/><rect x="118" y="30" width="16" height="40"/><rect x="136" y="42" width="18" height="28"/></g>
    <g fill="${c[0]}"><rect x="62" y="24" width="28" height="46"/><rect x="66" y="16" width="20" height="8"/><rect x="70" y="8" width="12" height="8"/><rect x="74.5" y="0" width="3" height="8"/>
    <rect x="94" y="20" width="18" height="50"/><path d="M94 20l9-10 9 10z"/><rect x="48" y="42" width="12" height="28"/></g>
    <g fill="#fff" opacity=".6"><rect x="67" y="30" width="3" height="4"/><rect x="74" y="30" width="3" height="4"/><rect x="81" y="30" width="3" height="4"/><rect x="67" y="40" width="3" height="4"/><rect x="74" y="40" width="3" height="4"/><rect x="81" y="40" width="3" height="4"/></g>
  </svg>`,
  tx: (c) => `<svg viewBox="0 0 160 70" width="160" height="70">
    <g fill="${c[1]}"><rect x="54" y="46" width="96" height="24"/></g>
    <g fill="${c[0]}"><rect x="88" y="36" width="28" height="10"/><path d="M86 36a16 16 0 0132 0z"/><rect x="100" y="8" width="4" height="12"/>
    <path d="M102 0l2 4.2 4.6.5-3.4 3.1 1 4.5-4.2-2.3-4.2 2.3 1-4.5-3.4-3.1 4.6-.5z"/></g>
    <g fill="#fff" opacity=".85"><rect x="60" y="52" width="4" height="14"/><rect x="70" y="52" width="4" height="14"/><rect x="80" y="52" width="4" height="14"/><rect x="120" y="52" width="4" height="14"/><rect x="130" y="52" width="4" height="14"/><rect x="140" y="52" width="4" height="14"/></g>
    <g fill="${c[0]}" opacity=".8"><rect x="18" y="40" width="6" height="30" rx="3"/><rect x="10" y="48" width="6" height="12" rx="3"/><rect x="26" y="44" width="6" height="12" rx="3"/></g>
  </svg>`,
  fl: (c) => `<svg viewBox="0 0 160 70" width="160" height="70">
    <circle cx="120" cy="22" r="14" fill="#fbbf24" opacity=".9"/>
    <path d="M0 70V58q20-8 40 0t40 0 40 0 40 0v12z" fill="${c[1]}"/>
    <g stroke="${c[0]}" stroke-width="3" fill="none"><path d="M60 62Q58 40 66 18"/><path d="M66 18c-12-2-17 5-19 10M66 18c12-4 17 2 19 8M66 18c-3-9-12-10-15-7M66 18c6-9 14-8 16-5"/>
    <path d="M92 62Q92 46 96 32"/><path d="M96 32c-9-2-13 3-15 7M96 32c9-3 13 2 15 7"/></g>
  </svg>`,
  il: (c) => `<svg viewBox="0 0 160 70" width="160" height="70">
    <g fill="${c[1]}"><rect x="10" y="40" width="16" height="30"/><rect x="28" y="30" width="14" height="40"/><rect x="128" y="34" width="20" height="36"/></g>
    <g fill="${c[0]}"><rect x="60" y="20" width="10" height="50"/><rect x="70" y="10" width="10" height="60"/><rect x="80" y="26" width="10" height="44"/><rect x="72" y="0" width="2" height="10"/><rect x="77" y="2" width="2" height="8"/>
    <rect x="98" y="18" width="16" height="52"/><path d="M98 18l8-8 8 8z"/><rect x="44" y="44" width="14" height="26"/></g>
  </svg>`,
  pa: (c) => `<svg viewBox="0 0 160 70" width="160" height="70">
    <g fill="${c[1]}"><rect x="40" y="44" width="96" height="26"/></g>
    <g fill="${c[0]}"><rect x="78" y="22" width="20" height="22"/><rect x="82" y="12" width="12" height="10"/><path d="M80 12l8-10 8 10z"/><circle cx="88" cy="30" r="4" fill="#fff"/></g>
    <g fill="#fff" opacity=".85"><rect x="46" y="50" width="5" height="9"/><rect x="58" y="50" width="5" height="9"/><rect x="113" y="50" width="5" height="9"/><rect x="125" y="50" width="5" height="9"/><rect x="84" y="54" width="8" height="16"/></g>
    <g transform="translate(14 30)" fill="${c[0]}" opacity=".85"><path d="M4 30c0-14 4-22 10-22s10 8 10 22z"/><rect x="12" y="2" width="4" height="6"/></g>
  </svg>`,
  ga: (c) => `<svg viewBox="0 0 160 70" width="160" height="70">
    <g fill="${c[1]}"><rect x="64" y="46" width="88" height="24"/></g>
    <g fill="${c[0]}"><rect x="96" y="36" width="24" height="10"/><path d="M94 36a14 14 0 0128 0z"/><rect x="106" y="12" width="4" height="10"/><circle cx="108" cy="10" r="3"/></g>
    <g fill="#fff" opacity=".85"><rect x="70" y="52" width="4" height="14"/><rect x="80" y="52" width="4" height="14"/><rect x="130" y="52" width="4" height="14"/><rect x="140" y="52" width="4" height="14"/></g>
    <circle cx="34" cy="44" r="16" fill="#fb923c"/><path d="M34 30q-4 12 0 28" stroke="#ea580c" stroke-width="2" fill="none" opacity=".6"/>
    <path d="M34 28c4-6 10-7 14-5-3 5-9 7-14 5z" fill="#16a34a"/>
  </svg>`
};

export interface FamilyTheme {
  artKey: string;
  code: string;
  colors: [string, string];
  bg: string;
  dot: string;
  blurb: string;
}

// Keyed by ExamCatalogEntry.id (src/data/examCatalog.ts).
export const FAMILY_THEMES: Record<string, FamilyTheme> = {
  'most-other-states': { artKey: 'us', code: 'US', colors: ['#3730a3', '#a5b4fc'], bg: 'linear-gradient(135deg,#e0e7ff,#eef2ff)', dot: '#1e2a78', blurb: 'ETS Praxis: Core, Elementary, PLT and Subject tests' },
  'california': { artKey: 'ca', code: 'CA', colors: ['#0284c7', '#7dd3fc'], bg: 'linear-gradient(135deg,#e0f2fe,#f0f9ff)', dot: '#0284c7', blurb: 'CBEST and CSET Multiple Subjects' },
  'paraprofessional-pathway': { artKey: 'pp', code: 'PP', colors: ['#0E9594', '#99f6e4'], bg: 'linear-gradient(135deg,#ccfbf1,#f0fdfa)', dot: '#0E9594', blurb: 'ETS ParaPathways: Reading/Writing and Math' },
  'new-york': { artKey: 'ny', code: 'NY', colors: ['#7c3aed', '#c4b5fd'], bg: 'linear-gradient(135deg,#ede9fe,#f5f3ff)', dot: '#7c3aed', blurb: 'NYSTCE: EAS and Multi-Subject CST' },
  'texas': { artKey: 'tx', code: 'TX', colors: ['#059669', '#86efac'], bg: 'linear-gradient(135deg,#dcfce7,#f0fdf4)', dot: '#059669', blurb: 'TExES Core Subjects and Pedagogy (PPR)' },
  'florida': { artKey: 'fl', code: 'FL', colors: ['#ea580c', '#fdba74'], bg: 'linear-gradient(135deg,#ffedd5,#fff7ed)', dot: '#ea580c', blurb: 'FTCE General Knowledge and Professional Ed' },
  'illinois': { artKey: 'il', code: 'IL', colors: ['#0891b2', '#67e8f9'], bg: 'linear-gradient(135deg,#cffafe,#ecfeff)', dot: '#0891b2', blurb: 'ILTS Licensure and Content Tests' },
  'pennsylvania': { artKey: 'pa', code: 'PA', colors: ['#9333ea', '#d8b4fe'], bg: 'linear-gradient(135deg,#f3e8ff,#faf5ff)', dot: '#9333ea', blurb: 'PECT (PreK-4) and PAPA' },
  'georgia': { artKey: 'ga', code: 'GA', colors: ['#d97706', '#fcd34d'], bg: 'linear-gradient(135deg,#fef3c7,#fffbeb)', dot: '#d97706', blurb: 'GACE Program Admission' }
};

const FALLBACK_THEME: FamilyTheme = { artKey: 'us', code: 'EX', colors: ['#334155', '#cbd5e1'], bg: 'linear-gradient(135deg,#f1f5f9,#f8fafc)', dot: '#334155', blurb: '' };

export function getFamilyTheme(entry: ExamCatalogEntry): FamilyTheme {
  return FAMILY_THEMES[entry.id] || { ...FALLBACK_THEME, code: entry.state.slice(0, 2).toUpperCase(), blurb: entry.examLabel };
}

export const FamilyArt: React.FC<{ entry: ExamCatalogEntry; className?: string }> = ({ entry, className }) => {
  const theme = getFamilyTheme(entry);
  const draw = ART[theme.artKey] || ART.us;
  return <span className={className} aria-hidden="true" dangerouslySetInnerHTML={{ __html: draw(theme.colors) }} />;
};

// Same family/paper matching heuristic used across the app (AuthModal,
// StudyGuides, ExamDetailPage, OnboardingFlow), kept in one place here for
// the two redesigned screens.
export function paperMatchesExamCode(paper: PraxisPaper, targetExamCodeRaw: string): boolean {
  const paperExamCode = paper.examCode ? String(paper.examCode).trim() : '';
  const targetExamCode = targetExamCodeRaw.trim();
  if (paperExamCode && paperExamCode.toLowerCase() === targetExamCode.toLowerCase()) return true;
  if (paperExamCode) {
    const lowerP = paperExamCode.toLowerCase();
    const lowerTarget = targetExamCode.toLowerCase();
    if (lowerP.startsWith(lowerTarget + '-') || lowerP === lowerTarget) return true;
  }
  if (paper.code) {
    const lowerCode = paper.code.trim().toLowerCase();
    const lowerTarget = targetExamCode.toLowerCase();
    if (lowerCode.startsWith(lowerTarget + '-') || lowerCode === lowerTarget) return true;
  }
  if (targetExamCode === 'Praxis-Core' && paper.category === 'core' && (!paperExamCode || paperExamCode.startsWith('Praxis'))) return true;
  if (targetExamCode === 'Praxis-Elementary' && (paper.category === 'elementary' || paper.code === '5001')) return true;
  if (targetExamCode === 'Praxis-Subject' && (paper.category === 'subject' || paper.category === 'elementary')) return true;
  if (targetExamCode === 'Praxis-PLT' && paper.category === 'plt') return true;
  return false;
}

export function isParaPathwaysPaper(p: PraxisPaper): boolean {
  return String(p.examCode || '').toLowerCase() === 'parapathways' || String(p.id || '').startsWith('ParaPathways');
}

// Real papers for a family: hidden papers and empty shells (no questions
// yet) are not counted, so a family only shows as "live" when students can
// actually practice. ParaPathways papers are excluded from the Praxis
// family on purpose (they have their own card), even though they share the
// generic 'subject' category.
export function papersForFamily(entry: ExamCatalogEntry, papers: PraxisPaper[]): PraxisPaper[] {
  const seen = new Set<string>();
  const out: PraxisPaper[] = [];
  const isPraxisFamily = entry.examCodes.some((c) => c.startsWith('Praxis-'));
  for (const p of papers) {
    if (!p || !p.id || seen.has(p.id)) continue;
    if ((p as any).visibleToStudents === false) continue;
    if (isPraxisFamily && isParaPathwaysPaper(p)) continue;
    if (!entry.examCodes.some((code) => paperMatchesExamCode(p, code))) continue;
    if (!paperHasContent(p)) continue;
    seen.add(p.id);
    out.push(p);
  }
  return out;
}

export function paperHasContent(p: PraxisPaper): boolean {
  const total = typeof p.totalQuestions === 'number' ? p.totalQuestions : 0;
  const embedded = Array.isArray(p.questions) ? p.questions.length : 0;
  return total > 0 || embedded > 0;
}

export type PaperTypeKey = 'core' | 'elementary' | 'subject' | 'plt' | 'para';

export function paperTypeOf(p: PraxisPaper): PaperTypeKey {
  if (isParaPathwaysPaper(p)) return 'para';
  if (p.category === 'core' || p.category === 'elementary' || p.category === 'plt') return p.category;
  return 'subject';
}
